import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { clearPendingCompletedSlideKey } from "@-/dev.improve.haniverse.lib.slide.read";
import { takeSlideCompletionPrior } from "#slide-completion-priors";
import { enqueueSlideCompletion } from "#slide-completion-queue";

/**
 * Put a slide's completion back the way it was before completion `completionId` (see
 * `completeSlide`) marked it — the inverse that lets Undo return the learner to a slide as if they
 * had never finished it. Without it the resolver keeps treating the slide as seen, and stepping back
 * and forward again skips it.
 *
 * A slide that was already complete gets its earlier timestamp back. One that was not loses its
 * completion: a favorited row keeps its favorite, and a row with neither timestamp is soft-deleted
 * rather than left lingering empty, as the favorite toggle does. The optimistic pending mark is
 * withdrawn too.
 *
 * Runs on the completion queue, behind the completion it reverses and any other still writing. So
 * it always finds that completion's record of the earlier state, even when Undo follows the swipe
 * before the row is written, and a completion queued before the undo cannot land after it and mark
 * the slide complete again.
 */
export function restoreSlideCompletion({
  actorDb,
  completionId,
  key,
  now,
}: {
  actorDb: ActorDatabase;
  completionId: string;
  key: string;
  now: Date;
}): Promise<void> {
  return enqueueSlideCompletion(async () => {
    const prior = takeSlideCompletionPrior(completionId);
    if (prior === undefined) {
      // The completion failed before it read the row, so it wrote nothing and its failure already
      // withdrew the pending mark — or its record aged out, and its earlier state is unknown.
      logger(import.meta.location).warn("No record of the slide completion to restore", {
        completionId,
        key,
      });
      return;
    }
    const { completedAtBefore } = prior;
    if (completedAtBefore === null) {
      clearPendingCompletedSlideKey(key);
    }
    const row = await actorDb(import.meta.location).query.userSlide.findFirst({
      columns: { favoritedAt: true, id: true },
      where: { key },
    });
    if (row === undefined) {
      return;
    }
    await actorDb(import.meta.location)
      .update(actorSchema.userSlide)
      .set(
        completedAtBefore !== null
          ? { completedAt: completedAtBefore }
          : row.favoritedAt === null
            ? { completedAt: null, deletedAt: now.toISOString() }
            : { completedAt: null },
      )
      .where(eq(actorSchema.userSlide.id, row.id))
      .run();
  });
}
